'use client'

/**
 * Blog diagrams, loaded only on the post that renders them.
 *
 * Both MDX maps (src/app/(app)/blogs/[slug]/page.tsx and
 * src/components/mdx-components.tsx) register every diagram so any post can use
 * any of them. Imported directly, that shipped all 44 diagram modules (about
 * 530 KB of JavaScript) with every blog post. next/dynamic only code-splits
 * when it is called from a Client Component, so the wrappers live here: each
 * one fetches its module on first render, a post downloads only the diagrams it
 * uses, and server rendering is unchanged (ssr stays on).
 *
 * To add a diagram: export a wrapper here, then register it in both MDX maps.
 */
import dynamic from 'next/dynamic'

export const BaseThreadDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.BaseThreadDiagram)
)
export const PThreadsDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.PThreadsDiagram)
)
export const CThreadsDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.CThreadsDiagram)
)
export const LThreadsDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.LThreadsDiagram)
)
export const FThreadsDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.FThreadsDiagram)
)
export const BThreadsDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.BThreadsDiagram)
)
export const ZThreadsDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.ZThreadsDiagram)
)
export const ThreadTypesOverview = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.ThreadTypesOverview)
)
export const CoreFourDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.CoreFourDiagram)
)
export const FourDimensionsDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.FourDimensionsDiagram)
)
export const ImplementationPathDiagram = dynamic(() =>
  import('./ThreadDiagrams').then(m => m.ImplementationPathDiagram)
)

export const DualRoutingDiagram = dynamic(() =>
  import('./GhostProtocolDiagrams').then(m => m.DualRoutingDiagram)
)
export const AgentRosterDiagram = dynamic(() =>
  import('./GhostProtocolDiagrams').then(m => m.AgentRosterDiagram)
)
export const GhostCoreFourDiagram = dynamic(() =>
  import('./GhostProtocolDiagrams').then(m => m.CoreFourDiagram)
)

export const TrustErosionDiagram = dynamic(() =>
  import('./TechDebtDiagrams').then(m => m.TrustErosionDiagram)
)
export const CodeQualityMetricsDiagram = dynamic(() =>
  import('./TechDebtDiagrams').then(m => m.CodeQualityMetricsDiagram)
)
export const SecuritySpikeDiagram = dynamic(() =>
  import('./TechDebtDiagrams').then(m => m.SecuritySpikeDiagram)
)
export const VibeCodingTradeoffsDiagram = dynamic(() =>
  import('./TechDebtDiagrams').then(m => m.VibeCodingTradeoffsDiagram)
)
export const ProductivityParadoxDiagram = dynamic(() =>
  import('./TechDebtDiagrams').then(m => m.ProductivityParadoxDiagram)
)
export const JuniorDeveloperSqueezeDiagram = dynamic(() =>
  import('./TechDebtDiagrams').then(m => m.JuniorDeveloperSqueezeDiagram)
)
export const CrisisTimelineDiagram = dynamic(() =>
  import('./TechDebtDiagrams').then(m => m.CrisisTimelineDiagram)
)
export const MitigationFrameworkDiagram = dynamic(() =>
  import('./TechDebtDiagrams').then(m => m.MitigationFrameworkDiagram)
)

export const GovernancePipelineDiagram = dynamic(() =>
  import('./ThreadGovernanceDiagrams').then(m => m.GovernancePipelineDiagram)
)
export const FourDimensionsGrid = dynamic(() =>
  import('./ThreadGovernanceDiagrams').then(m => m.FourDimensionsGrid)
)
export const ImplementationTimeline = dynamic(() =>
  import('./ThreadGovernanceDiagrams').then(m => m.ImplementationTimeline)
)
export const ComplianceChecklist = dynamic(() =>
  import('./ThreadGovernanceDiagrams').then(m => m.ComplianceChecklist)
)
export const MetricsComparisonDiagram = dynamic(() =>
  import('./ThreadGovernanceDiagrams').then(m => m.MetricsComparisonDiagram)
)
export const StrategicPositioningDiagram = dynamic(() =>
  import('./ThreadGovernanceDiagrams').then(m => m.StrategicPositioningDiagram)
)

export const ClaudeMdImpactDiagram = dynamic(() =>
  import('./ClaudeCodeDiagrams').then(m => m.ClaudeMdImpactDiagram)
)
export const VulnerabilityByLanguageDiagram = dynamic(() =>
  import('./ClaudeCodeDiagrams').then(m => m.VulnerabilityByLanguageDiagram)
)
export const SecurityToolMatrixDiagram = dynamic(() =>
  import('./ClaudeCodeDiagrams').then(m => m.SecurityToolMatrixDiagram)
)
export const SixElementsFrameworkDiagram = dynamic(() =>
  import('./ClaudeCodeDiagrams').then(m => m.SixElementsFrameworkDiagram)
)
export const NinetyDayRoadmapDiagram = dynamic(() =>
  import('./ClaudeCodeDiagrams').then(m => m.NinetyDayRoadmapDiagram)
)
export const MetricsDashboardDiagram = dynamic(() =>
  import('./ClaudeCodeDiagrams').then(m => m.MetricsDashboardDiagram)
)
export const PromptComparisonDiagram = dynamic(() =>
  import('./ClaudeCodeDiagrams').then(m => m.PromptComparisonDiagram)
)

export const TimelineCompressionDiagram = dynamic(() =>
  import('./LakbayAICaseStudyDiagrams').then(m => m.TimelineCompressionDiagram)
)
export const ScopeDeliveredDiagram = dynamic(() =>
  import('./LakbayAICaseStudyDiagrams').then(m => m.ScopeDeliveredDiagram)
)
export const ZThreadFlowDiagram = dynamic(() =>
  import('./LakbayAICaseStudyDiagrams').then(m => m.ZThreadFlowDiagram)
)
export const GovernanceScorecardDiagram = dynamic(() =>
  import('./LakbayAICaseStudyDiagrams').then(m => m.GovernanceScorecardDiagram)
)
export const CostCompressionDiagram = dynamic(() =>
  import('./LakbayAICaseStudyDiagrams').then(m => m.CostCompressionDiagram)
)
export const ThreadProgressionDiagram = dynamic(() =>
  import('./LakbayAICaseStudyDiagrams').then(m => m.ThreadProgressionDiagram)
)
export const TechStackDiagram = dynamic(() =>
  import('./LakbayAICaseStudyDiagrams').then(m => m.TechStackDiagram)
)

export const OriginStoryTimeline = dynamic(() =>
  import('./OriginStoryDiagrams').then(m => m.OriginStoryTimeline)
)

export const AgencyModelComparisonDiagram = dynamic(() =>
  import('./AINativeAgencyDiagrams').then(m => m.AgencyModelComparisonDiagram)
)
export const TimelineComparisonDiagram = dynamic(() =>
  import('./AINativeAgencyDiagrams').then(m => m.TimelineComparisonDiagram)
)
export const CostComparisonDiagram = dynamic(() =>
  import('./AINativeAgencyDiagrams').then(m => m.CostComparisonDiagram)
)
export const AgencyTransformationDiagram = dynamic(() =>
  import('./AINativeAgencyDiagrams').then(m => m.AgencyTransformationDiagram)
)

export const EmotionalValueRoutingDiagram = dynamic(() =>
  import('./CustomerServiceDiagrams').then(m => m.EmotionalValueRoutingDiagram)
)
export const CustomerServiceStatsDiagram = dynamic(() =>
  import('./CustomerServiceDiagrams').then(m => m.CustomerServiceStatsDiagram)
)
export const HybridROIDiagram = dynamic(() =>
  import('./CustomerServiceDiagrams').then(m => m.HybridROIDiagram)
)

export const SEADigitalEconomyDiagram = dynamic(() =>
  import('./SEAUXDiagrams').then(m => m.SEADigitalEconomyDiagram)
)
export const CulturalColorMapDiagram = dynamic(() =>
  import('./SEAUXDiagrams').then(m => m.CulturalColorMapDiagram)
)
export const PaymentEcosystemDiagram = dynamic(() =>
  import('./SEAUXDiagrams').then(m => m.PaymentEcosystemDiagram)
)
export const MobileInfrastructureDiagram = dynamic(() =>
  import('./SEAUXDiagrams').then(m => m.MobileInfrastructureDiagram)
)

export const PhilippineDigitalLandscapeDiagram = dynamic(() =>
  import('./PhilippinesUXDiagrams').then(
    m => m.PhilippineDigitalLandscapeDiagram
  )
)
export const PhilippinePaymentsDiagram = dynamic(() =>
  import('./PhilippinesUXDiagrams').then(m => m.PhilippinePaymentsDiagram)
)
export const FilipinoMobileBehaviorDiagram = dynamic(() =>
  import('./PhilippinesUXDiagrams').then(m => m.FilipinoMobileBehaviorDiagram)
)

export const ContextHierarchyDiagram = dynamic(() =>
  import('./ContextEngineeringDiagrams').then(m => m.ContextHierarchyDiagram)
)
export const ContextWindowOptimizationDiagram = dynamic(() =>
  import('./ContextEngineeringDiagrams').then(
    m => m.ContextWindowOptimizationDiagram
  )
)
export const ContextRotDiagram = dynamic(() =>
  import('./ContextEngineeringDiagrams').then(m => m.ContextRotDiagram)
)
export const ThreadKitCTA = dynamic(() =>
  import('./ContextEngineeringDiagrams').then(m => m.ThreadKitCTA)
)

export const SlopsquattingAttackFlowDiagram = dynamic(() =>
  import('./SlopsquattingDiagrams').then(m => m.SlopsquattingAttackFlowDiagram)
)
export const DefenseLayersDiagram = dynamic(() =>
  import('./SlopsquattingDiagrams').then(m => m.DefenseLayersDiagram)
)
export const SupplyChainThreatLandscapeDiagram = dynamic(() =>
  import('./SlopsquattingDiagrams').then(
    m => m.SupplyChainThreatLandscapeDiagram
  )
)

export const HookLifecycleDiagram = dynamic(() =>
  import('./ClaudeCodeHooksDiagrams').then(m => m.HookLifecycleDiagram)
)
export const ThreeHandlerTypesDiagram = dynamic(() =>
  import('./ClaudeCodeHooksDiagrams').then(m => m.ThreeHandlerTypesDiagram)
)
export const GovernanceLoopDiagram = dynamic(() =>
  import('./ClaudeCodeHooksDiagrams').then(m => m.GovernanceLoopDiagram)
)

export const LeadFlowComparisonDiagram = dynamic(() =>
  import('./AILeadQualificationDiagrams').then(m => m.LeadFlowComparisonDiagram)
)
export const LeadCostComparisonDiagram = dynamic(() =>
  import('./AILeadQualificationDiagrams').then(m => m.LeadCostComparisonDiagram)
)
export const LeadScoringDiagram = dynamic(() =>
  import('./AILeadQualificationDiagrams').then(m => m.LeadScoringDiagram)
)
export const SpeedToLeadDiagram = dynamic(() =>
  import('./AILeadQualificationDiagrams').then(m => m.SpeedToLeadDiagram)
)
export const ContactFormCTA = dynamic(() =>
  import('./AILeadQualificationDiagrams').then(m => m.ContactFormCTA)
)

export const EntityResolutionDiagram = dynamic(() =>
  import('./BrandDisambiguationDiagrams').then(m => m.EntityResolutionDiagram)
)
export const DisambiguationVsHallucinationDiagram = dynamic(() =>
  import('./BrandDisambiguationDiagrams').then(
    m => m.DisambiguationVsHallucinationDiagram
  )
)

export const YouTubeAIStackDiagram = dynamic(() =>
  import('./YouTubeAIStackDiagrams').then(m => m.YouTubeAIStackDiagram)
)
export const YouTubeBrandMonitorCTA = dynamic(() =>
  import('./YouTubeAIStackDiagrams').then(m => m.YouTubeBrandMonitorCTA)
)

export const AEOReachableAnswerableDiagram = dynamic(() =>
  import('./AEOFailureDiagrams').then(m => m.AEOReachableAnswerableDiagram)
)

export const RadarFixLoopDiagram = dynamic(() =>
  import('./RadarFixLoopDiagrams').then(m => m.RadarFixLoopDiagram)
)

export const YouTubeDimensionsDiagram = dynamic(() =>
  import('./YouTubeMonitorDiagrams').then(m => m.YouTubeDimensionsDiagram)
)

export const RedditSeedingTiersDiagram = dynamic(() =>
  import('./RedditSeedingDiagrams').then(m => m.RedditSeedingTiersDiagram)
)

export const ComparisonBarsDiagram = dynamic(() =>
  import('./ReusableBlogDiagrams').then(m => m.ComparisonBarsDiagram)
)
export const ProgressionStepsDiagram = dynamic(() =>
  import('./ReusableBlogDiagrams').then(m => m.ProgressionStepsDiagram)
)
export const ShareDonutDiagram = dynamic(() =>
  import('./ReusableBlogDiagrams').then(m => m.ShareDonutDiagram)
)
export const BeforeAfterDiagram = dynamic(() =>
  import('./ReusableBlogDiagrams').then(m => m.BeforeAfterDiagram)
)
export const StatHighlightGrid = dynamic(() =>
  import('./ReusableBlogDiagrams').then(m => m.StatHighlightGrid)
)

export const DirectedGridLoopDiagram = dynamic(() =>
  import('./DirectedGridDiagrams').then(m => m.DirectedGridLoopDiagram)
)
export const DirectedGridVerbsDiagram = dynamic(() =>
  import('./DirectedGridDiagrams').then(m => m.DirectedGridVerbsDiagram)
)
export const AutonomyLadderDiagram = dynamic(() =>
  import('./DirectedGridDiagrams').then(m => m.AutonomyLadderDiagram)
)
export const HumanCheckpointDiagram = dynamic(() =>
  import('./DirectedGridDiagrams').then(m => m.HumanCheckpointDiagram)
)

export const ZeroClickProgressionDiagram = dynamic(() =>
  import('./AISearchShiftDiagrams').then(m => m.ZeroClickProgressionDiagram)
)
export const ConversionComparisonDiagram = dynamic(() =>
  import('./AISearchShiftDiagrams').then(m => m.ConversionComparisonDiagram)
)
export const AISearchMarketShareDiagram = dynamic(() =>
  import('./AISearchShiftDiagrams').then(m => m.AISearchMarketShareDiagram)
)
export const IndustryImpactDiagram = dynamic(() =>
  import('./AISearchShiftDiagrams').then(m => m.IndustryImpactDiagram)
)
export const DiscoveryVsSelectionDiagram = dynamic(() =>
  import('./AISearchShiftDiagrams').then(m => m.DiscoveryVsSelectionDiagram)
)

export const ThreeDisciplinesDiagram = dynamic(() =>
  import('./SEOvsGEODiagrams').then(m => m.ThreeDisciplinesDiagram)
)
export const GenerativeEngineTypesDiagram = dynamic(() =>
  import('./SEOvsGEODiagrams').then(m => m.GenerativeEngineTypesDiagram)
)
export const GEORankingFactorsDiagram = dynamic(() =>
  import('./SEOvsGEODiagrams').then(m => m.GEORankingFactorsDiagram)
)
export const OverlapProblemDiagram = dynamic(() =>
  import('./SEOvsGEODiagrams').then(m => m.OverlapProblemDiagram)
)
export const StrategyDecisionDiagram = dynamic(() =>
  import('./SEOvsGEODiagrams').then(m => m.StrategyDecisionDiagram)
)
export const PlatformCitationDiagram = dynamic(() =>
  import('./SEOvsGEODiagrams').then(m => m.PlatformCitationDiagram)
)

export const AICrawlerEcosystem = dynamic(() =>
  import('./GEOPlaybookDiagrams').then(m => m.AICrawlerEcosystem)
)
export const GEOTacticsLift = dynamic(() =>
  import('./GEOPlaybookDiagrams').then(m => m.GEOTacticsLift)
)
export const TechnicalGEOChecklist = dynamic(() =>
  import('./GEOPlaybookDiagrams').then(m => m.TechnicalGEOChecklist)
)
export const CaseStudyCards = dynamic(() =>
  import('./GEOPlaybookDiagrams').then(m => m.CaseStudyCards)
)
export const AITrafficMeasurement = dynamic(() =>
  import('./GEOPlaybookDiagrams').then(m => m.AITrafficMeasurement)
)

export const GEOImplementationAudit = dynamic(() =>
  import('./AISearchResultsDiagrams').then(m => m.GEOImplementationAudit)
)
export const ContentClusterStrategy = dynamic(() =>
  import('./AISearchResultsDiagrams').then(m => m.ContentClusterStrategy)
)
export const TechnicalGEOStack = dynamic(() =>
  import('./AISearchResultsDiagrams').then(m => m.TechnicalGEOStack)
)
export const GEOLessonsLearned = dynamic(() =>
  import('./AISearchResultsDiagrams').then(m => m.GEOLessonsLearned)
)

export const BrandAuthorityFramework = dynamic(() =>
  import('./BrandingGEODiagrams').then(m => m.BrandAuthorityFramework)
)
export const AIBrandStoryFramework = dynamic(() =>
  import('./BrandingGEODiagrams').then(m => m.AIBrandStoryFramework)
)
export const BrandAICitationGap = dynamic(() =>
  import('./BrandingGEODiagrams').then(m => m.BrandAICitationGap)
)

export const AXCapabilitiesDiagram = dynamic(() =>
  import('./AgenticUXDiagrams').then(m => m.AXCapabilitiesDiagram)
)
export const AgenticPatternLifecycleDiagram = dynamic(() =>
  import('./AgenticUXDiagrams').then(m => m.AgenticPatternLifecycleDiagram)
)
export const AgenticProtocolStackDiagram = dynamic(() =>
  import('./AgenticUXDiagrams').then(m => m.AgenticProtocolStackDiagram)
)
export const DesignerEvolutionDiagram = dynamic(() =>
  import('./AgenticUXDiagrams').then(m => m.DesignerEvolutionDiagram)
)
export const ProductRedesignDiagram = dynamic(() =>
  import('./AgenticUXDiagrams').then(m => m.ProductRedesignDiagram)
)

export const LLMVisibilityStack = dynamic(() =>
  import('./LLMVisibilityDiagrams').then(m => m.LLMVisibilityStack)
)
export const EntityLinkingDiagram = dynamic(() =>
  import('./LLMVisibilityDiagrams').then(m => m.EntityLinkingDiagram)
)
export const BeforeAfterMetrics = dynamic(() =>
  import('./LLMVisibilityDiagrams').then(m => m.BeforeAfterMetrics)
)
export const RAGPipelineDiagram = dynamic(() =>
  import('./LLMVisibilityDiagrams').then(m => m.RAGPipelineDiagram)
)

export const TalentPipelineCrisisDiagram = dynamic(() =>
  import('./JuniorDevExtinctionDiagrams').then(
    m => m.TalentPipelineCrisisDiagram
  )
)
export const ApprenticeshipROIDiagram = dynamic(() =>
  import('./JuniorDevExtinctionDiagrams').then(m => m.ApprenticeshipROIDiagram)
)
export const HiringDecisionMatrixDiagram = dynamic(() =>
  import('./JuniorDevExtinctionDiagrams').then(
    m => m.HiringDecisionMatrixDiagram
  )
)
export const KnowledgeTransferBreakdownDiagram = dynamic(() =>
  import('./JuniorDevExtinctionDiagrams').then(
    m => m.KnowledgeTransferBreakdownDiagram
  )
)

export const OwnershipModelComparisonDiagram = dynamic(() =>
  import('./PhilippinesAIDiagrams').then(m => m.OwnershipModelComparisonDiagram)
)
export const TimezoneOverlapDiagram = dynamic(() =>
  import('./PhilippinesAIDiagrams').then(m => m.TimezoneOverlapDiagram)
)
export const EngagementPathwayDiagram = dynamic(() =>
  import('./PhilippinesAIDiagrams').then(m => m.EngagementPathwayDiagram)
)
export const CapabilityMatrixDiagram = dynamic(() =>
  import('./PhilippinesAIDiagrams').then(m => m.CapabilityMatrixDiagram)
)

export const AIVisibilityToolkitWorkflow = dynamic(() =>
  import('./AIVisibilityToolsDiagrams').then(m => m.AIVisibilityToolkitWorkflow)
)
export const AIVisibilityScoreComparison = dynamic(() =>
  import('./AIVisibilityToolsDiagrams').then(m => m.AIVisibilityScoreComparison)
)
export const AIVisibilityFrequencyGuide = dynamic(() =>
  import('./AIVisibilityToolsDiagrams').then(m => m.AIVisibilityFrequencyGuide)
)
export const ToolCTACard = dynamic(() =>
  import('./AIVisibilityToolsDiagrams').then(m => m.ToolCTACard)
)

export const DiscoveryChainDiagram = dynamic(() =>
  import('./AIDiscoverabilityDiagrams').then(m => m.DiscoveryChainDiagram)
)
export const ScrapedVsCitedDiagram = dynamic(() =>
  import('./AIDiscoverabilityDiagrams').then(m => m.ScrapedVsCitedDiagram)
)
export const FAQAutomationDiagram = dynamic(() =>
  import('./AIDiscoverabilityDiagrams').then(m => m.FAQAutomationDiagram)
)
export const KnowledgeAPIResponseDiagram = dynamic(() =>
  import('./AIDiscoverabilityDiagrams').then(m => m.KnowledgeAPIResponseDiagram)
)

export const BotCategoryDiagram = dynamic(() =>
  import('./BotSegmentationDiagrams').then(m => m.BotCategoryDiagram)
)
export const CrawlToClickDiagram = dynamic(() =>
  import('./BotSegmentationDiagrams').then(m => m.CrawlToClickDiagram)
)
export const SegmentationFlowDiagram = dynamic(() =>
  import('./BotSegmentationDiagrams').then(m => m.SegmentationFlowDiagram)
)
export const BlockVsAllowOutcomeDiagram = dynamic(() =>
  import('./BotSegmentationDiagrams').then(m => m.BlockVsAllowOutcomeDiagram)
)

export const TwoThreadsDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.TwoThreadsDiagram)
)
export const AXFrameworkDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.AXFrameworkDiagram)
)
export const AXvsUXDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.AXvsUXDiagram)
)
export const AXMaturityDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.AXMaturityDiagram)
)
export const TrustProgressionDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.TrustProgressionDiagram)
)
export const TrustBreakdownDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.TrustBreakdownDiagram)
)
export const ProgressiveAutonomyDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.ProgressiveAutonomyDiagram)
)
export const TrustGapDataDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.TrustGapDataDiagram)
)
export const MultiTurnDegradationDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.MultiTurnDegradationDiagram)
)
export const ConversationStateMachineDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.ConversationStateMachineDiagram)
)
export const ContextPersistenceDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.ContextPersistenceDiagram)
)
export const HandoffTopologyDiagram = dynamic(() =>
  import('./AXPlaybookDiagrams').then(m => m.HandoffTopologyDiagram)
)

export const PersonalityMatrixDiagram = dynamic(() =>
  import('./AgentPersonalityDiagrams').then(m => m.PersonalityMatrixDiagram)
)
export const ArchetypeSpectrumDiagram = dynamic(() =>
  import('./AgentPersonalityDiagrams').then(m => m.ArchetypeSpectrumDiagram)
)
export const ToneCalibrationDiagram = dynamic(() =>
  import('./AgentPersonalityDiagrams').then(m => m.ToneCalibrationDiagram)
)

export const AXMetricsFrameworkDiagram = dynamic(() =>
  import('./AXMetricsDiagrams').then(m => m.AXMetricsFrameworkDiagram)
)
export const TrustCalibrationDiagram = dynamic(() =>
  import('./AXMetricsDiagrams').then(m => m.TrustCalibrationDiagram)
)
export const AutonomyLevelsDiagram = dynamic(() =>
  import('./AXMetricsDiagrams').then(m => m.AutonomyLevelsDiagram)
)
export const AXMetricsDashboardDiagram = dynamic(() =>
  import('./AXMetricsDiagrams').then(m => m.AXMetricsDashboardDiagram)
)

export const VisibilityStackDiagram = dynamic(() =>
  import('./AIVisibilityStackDiagrams').then(m => m.VisibilityStackDiagram)
)

export const AEOImplementationStack = dynamic(() =>
  import('./AEOImplementationDiagrams').then(m => m.AEOImplementationStack)
)
export const AEOBeforeAfterComparison = dynamic(() =>
  import('./AEOImplementationDiagrams').then(m => m.AEOBeforeAfterComparison)
)

export const ThreadAutonomyTrustMap = dynamic(() =>
  import('./TBEAXDDiagrams').then(m => m.ThreadAutonomyTrustMap)
)
export const ShadowToSteerToSelfDiagram = dynamic(() =>
  import('./TBEAXDDiagrams').then(m => m.ShadowToSteerToSelfDiagram)
)
export const PatternsAntiPatternsDiagram = dynamic(() =>
  import('./TBEAXDDiagrams').then(m => m.PatternsAntiPatternsDiagram)
)

export const RadarPositioningDiagram = dynamic(() =>
  import('./RadarV2Diagrams').then(m => m.RadarPositioningDiagram)
)
export const SourceInfluenceMockup = dynamic(() =>
  import('./RadarV2Diagrams').then(m => m.SourceInfluenceMockup)
)
export const SOVDashboardMockup = dynamic(() =>
  import('./RadarV2Diagrams').then(m => m.SOVDashboardMockup)
)
export const HallucinationFlagsMockup = dynamic(() =>
  import('./RadarV2Diagrams').then(m => m.HallucinationFlagsMockup)
)
export const SentimentAnalysisMockup = dynamic(() =>
  import('./RadarV2Diagrams').then(m => m.SentimentAnalysisMockup)
)
export const CompetitiveComparisonDiagram = dynamic(() =>
  import('./RadarV2Diagrams').then(m => m.CompetitiveComparisonDiagram)
)
export const CrossToolConflictDiagram = dynamic(() =>
  import('./RadarV2Diagrams').then(m => m.CrossToolConflictDiagram)
)

export const UserJourneyFunnelDiagram = dynamic(() =>
  import('./AIVisibilityStackUsersDiagrams').then(
    m => m.UserJourneyFunnelDiagram
  )
)
export const CrossToolConflictsDiagram = dynamic(() =>
  import('./AIVisibilityStackUsersDiagrams').then(
    m => m.CrossToolConflictsDiagram
  )
)

export const FiveLayersDiagram = dynamic(() =>
  import('./AIVisibilityGapDiagrams').then(m => m.FiveLayersDiagram)
)
export const SEOvsAIAuditDiagram = dynamic(() =>
  import('./AIVisibilityGapDiagrams').then(m => m.SEOvsAIAuditDiagram)
)
